// Chat UI used inside the right-side Drawer — Pearl / Vela / models.
// Captain 2026-04-23: conversations persist across refresh via
// localStorage keyed on the caller-supplied storageKey (seat id /
// model id). Waytide's ethos is that claims are evidenced and state
// is durable; throwing away the drawer transcript on refresh
// contradicted that. Backend persistence (SDB) can follow later; this
// keeps the UX durable today without touching the schema.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import {
  ApiError,
  type ChatMessage,
  type ChatResponse,
} from "../../api/client";
import { triggerBalanceRefresh } from "../../chrome/OpenRouterBalance";
import { ChatInput } from "../ChatInput";
import { MessageCard } from "../MessageCard";

/** Drawer message shape — user messages carry only content;
 * assistant messages also carry per-turn usage/duration so the
 * cost/speed footer under the bubble can render. */
interface DrawerMessage extends ChatMessage {
  input_tokens?: number;
  output_tokens?: number;
  cost_usd_micros?: number;
  duration_ms?: number;
}

interface Props {
  placeholder: string;
  onSend: (messages: ChatMessage[]) => Promise<ChatResponse>;
  /** Called when an assistant reply lands with a suggestion the caller
   * wants to apply (e.g. Pearl suggesting persona fields). Not used
   * for pure browse-chat — callers that don't need it omit this. */
  onAssistant?: (content: string) => void;
  /** Optional opening message from the assistant (seat greeting). */
  greeting?: string;
  /** localStorage key for the conversation. Pass a stable id per
   * identity (e.g. "seat-vela" or "model-openai/gpt-5.4-pro") so
   * each seat/model keeps its own thread. If undefined, the chat
   * falls back to session-only memory (discouraged). */
  storageKey?: string;
  /** Label shown above assistant bubbles. Defaults to "assistant"
   * for backwards compat; drawer callers pass the actual seat /
   * model name. */
  assistantLabel?: string;
  testId?: string;
}

const STORAGE_PREFIX = "wt_drawer_chat:";

function loadStored(
  storageKey: string | undefined,
): DrawerMessage[] | null {
  if (!storageKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (m): m is DrawerMessage =>
        m &&
        (m.role === "user" || m.role === "assistant") &&
        typeof m.content === "string",
    );
  } catch {
    return null;
  }
}

function saveStored(
  storageKey: string | undefined,
  messages: DrawerMessage[],
) {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + storageKey,
      JSON.stringify(messages),
    );
  } catch {
    // quota / private-mode — in-memory only for the session.
  }
}

function formatCost(micros: number): string {
  const usd = micros / 1_000_000;
  if (usd === 0) return "—";
  if (usd >= 0.01) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(4)}`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}


export function SimpleChat({
  placeholder,
  onSend,
  onAssistant,
  greeting,
  storageKey,
  assistantLabel = "assistant",
  testId,
}: Props) {
  const [messages, setMessages] = useState<DrawerMessage[]>(() => {
    const stored = loadStored(storageKey);
    if (stored && stored.length > 0) return stored;
    return greeting ? [{ role: "assistant", content: greeting }] : [];
  });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Two-stage delete for drawer messages — mirrors the main chat.
  const [armedDeleteIdx, setArmedDeleteIdx] = useState<number | null>(null);
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Persist every time the transcript changes.
  useEffect(() => {
    saveStored(storageKey, messages);
  }, [messages, storageKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  function deleteMessage(idx: number) {
    if (armedDeleteIdx === idx) {
      setArmedDeleteIdx(null);
      if (armTimerRef.current) clearTimeout(armTimerRef.current);
      setMessages((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    setArmedDeleteIdx(idx);
    if (armTimerRef.current) clearTimeout(armTimerRef.current);
    armTimerRef.current = setTimeout(() => setArmedDeleteIdx(null), 3000);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setDraft("");
    setErr("");
    const nextMessages: DrawerMessage[] = [
      ...messages,
      { role: "user", content: text },
    ];
    setMessages(nextMessages);
    setBusy(true);
    try {
      const reply = await onSend(nextMessages);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: reply.content,
          input_tokens: reply.input_tokens,
          output_tokens: reply.output_tokens,
          cost_usd_micros: reply.cost_usd_micros,
          duration_ms: reply.duration_ms,
        },
      ]);
      onAssistant?.(reply.content);
      triggerBalanceRefresh();
    } catch (e: unknown) {
      if (e instanceof ApiError) setErr(e.message);
      else setErr(e instanceof Error ? e.message : "Chat failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col" data-testid={testId}>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3"
      >
        {messages.length === 0 && (
          <p className="text-sm text-muted">No messages yet.</p>
        )}
        <ul className="flex flex-col">
          {messages.map((m, i) => {
            const prev = messages[i - 1];
            const sameAuthor = prev !== undefined && prev.role === m.role;
            const isMe = m.role === "user";
            const hasUsage =
              m.role === "assistant" &&
              (m.duration_ms !== undefined ||
                m.cost_usd_micros !== undefined ||
                m.output_tokens !== undefined);
            return (
              <MessageCard
                key={i}
                author={{
                  name: isMe ? "you" : assistantLabel,
                  colorClass: isMe ? "text-primary" : "text-accent",
                }}
                alignment={isMe ? "me" : "other"}
                sameAuthor={sameAuthor}
                onDelete={() => deleteMessage(i)}
                deleteArmed={armedDeleteIdx === i}
                testId={`drawer-message-${m.role}`}
              >
                <div className="prose prose-sm max-w-none wt-prose">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight]}
                  >
                    {m.content}
                  </ReactMarkdown>
                </div>
                {hasUsage && (
                  <p
                    className="mt-1 text-right font-code text-[0.6875rem] text-faint"
                    data-testid={`drawer-cost-badge-${i}`}
                  >
                    {formatCost(m.cost_usd_micros ?? 0)}
                    {(m.input_tokens ?? 0) > 0 || (m.output_tokens ?? 0) > 0
                      ? ` · ${m.input_tokens ?? 0}↑ ${m.output_tokens ?? 0}↓`
                      : ""}
                    {m.duration_ms !== undefined
                      ? ` · ${formatDuration(m.duration_ms)}`
                      : ""}
                  </p>
                )}
              </MessageCard>
            );
          })}
          {busy && (
            <MessageCard
              author={{ name: assistantLabel, colorClass: "text-accent" }}
              alignment="other"
              testId="drawer-message-thinking"
            >
              <p className="text-sm text-muted">Thinking…</p>
            </MessageCard>
          )}
        </ul>
      </div>
      {/* Wrapper matches the main chat Composer (px-4 pt-0.5 pb-4
       * + inflight strip h-4 above the ChatInput). Drawer has no
       * inflight strip to render, so pt-5 replaces the strip's
       * reserved height. Horizontal padding + bottom padding match
       * main chat exactly — ChatInput itself is already the shared
       * component so height + internal padding are identical. */}
      <form
        onSubmit={submit}
        className="bg-transparent px-4 pt-5 pb-4"
      >
        <ChatInput
          value={draft}
          onChange={(value) => setDraft(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit(e as unknown as React.FormEvent);
            }
          }}
          placeholder={placeholder}
          busy={busy}
        />
      </form>
      {err && (
        <p className="px-3 py-1 text-xs">
          {/^(no[_ ]openrouter[_ ]key|paste an openrouter key|428|precondition)/i.test(err) ? (
            <Link
              to="?tab=settings#provider-keys"
              className="cursor-pointer text-warning underline decoration-dotted underline-offset-2 hover:text-primary"
            >
              Add an OpenRouter key
            </Link>
          ) : (
            <span className="text-danger">{err}</span>
          )}
        </p>
      )}
    </div>
  );
}
